import * as React from 'react';

import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';

import {
  black,
  errorColor,
  gray100,
  gray90,
  gray80,
  gray10
} from '../../constants/Colors';
import { formLabelWrapper } from '../../constants/Location';

import type { FieldError } from 'react-hook-form';

type OutlinedInputProps = React.ComponentProps<typeof OutlinedInput>;

type Props = OutlinedInputProps & {
  disabled?: boolean;
  labelText?: string;
  requiredText?: string;
  error?: boolean;
  helperText?: string | FieldError | undefined;
  fieldType?: string;
  width?: string;
  spHeight?: string;
  spWidth?: string;
  formMb?: string | undefined;
  backgroundColor?: string;
  attentionText?: string;
};

const CustomTextfield = React.forwardRef(
  (props: Props, ref): React.ReactElement => {
    const {
      disabled,
      labelText,
      requiredText,
      error,
      helperText,
      formMb,
      fieldType,
      width,
      spWidth,
      spHeight,
      attentionText,
      ...rest
    } = props;

    function inputBorderFocusColor(): string {
      return error ? errorColor : gray100;
    }

    function inputBorderColor(): string {
      return error ? errorColor : gray80;
    }

    let inputHeight;
    if (fieldType === 'textarea') {
      inputHeight = '128px';
    } else if (fieldType === 'textareaRow10') {
      inputHeight = '280px';
    } else if (fieldType === 'textareaRow9') {
      inputHeight = '250px';
    } else if (fieldType === 'textareaRow8') {
      inputHeight = '220px';
    } else if (fieldType === 'textareaRow7') {
      inputHeight = '190px';
    } else if (fieldType === 'textareaRow6') {
      inputHeight = '160px';
    } else if (fieldType === 'textareaRow5') {
      inputHeight = '138px';
    } else if (fieldType === 'textareaRow4') {
      inputHeight = '120px';
    } else if (fieldType === 'textareaRow3') {
      inputHeight = '88px';
    } else if (fieldType === 'textareaRow2') {
      inputHeight = '64px';
    } else {
      inputHeight = '40px';
    }

    const styles = {
      formLabelWrapper,
      helper: {
        width: width ?? '100%',
        '@media screen and (max-width:960px)': {
          marginLeft: 0,
          width: spWidth ?? '100%'
        }
      },
      input: {
        '& .MuiInputBase-input': {
          WebkitTextFillColor: disabled ? gray90 : gray100
        },
        '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: inputBorderColor()
        },
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: inputBorderFocusColor(),
            borderWidth: '1px'
          },
        '&.MuiOutlinedInput-root:hover': {
          borderColor: inputBorderFocusColor(),
          borderWidth: '1px'
        },
        '@media screen and (max-width:960px)': {
          width: spWidth ?? '100%'
        },
        bgcolor: disabled ? gray10 : '#FFF',
        '@media screen and (max-width:600px)': {
          height: spHeight ?? inputHeight
        },
        height: inputHeight,
        width: width ?? '560px'
      },
      label: {
        fontSize: '14px'
      },
      required: {
        color: errorColor,
        fontSize: '14px',
        marginLeft: '8px'
      },
      formMb: { marginBottom: formMb ?? '8px' }
    };

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <Typography sx={styles.formMb} align="left" component="div">
        {labelText && (
          <Typography align="left" sx={styles.formLabelWrapper} component="div">
            <Typography color={black} sx={styles.label} component="span">
              {labelText}
            </Typography>

            {requiredText && (
              <Typography component="span" sx={styles.required}>
                {requiredText}
              </Typography>
            )}
          </Typography>
        )}
        <OutlinedInput
          {...rest}
          value={rest.value ?? ''}
          sx={styles.input}
          ref={ref}
          autoComplete="off"
          disabled={disabled}
        />
        {error && (
          <FormHelperText sx={styles.helper} error={error}>
            {error && textfieldHelper()}
          </FormHelperText>
        )}
        {attentionText && (
          <FormHelperText sx={{}} error={error}>
            {attentionText}
          </FormHelperText>
        )}
      </Typography>
    );
  }
);
CustomTextfield.displayName = 'CustomTextfield';
export default CustomTextfield;
